/**
 * F-0821 / I-2627: this Mac has two Tailscale logins, and so two MagicDNS names. Only the active
 * login's name answers; the other times out. Clients find whichever answers, with no setting.
 *
 * ⛔ The list is the whole of the trust. A client may move a request, and the credentials on it, only
 * between the exact names below: never a suffix match (`evil.ts.net`, `ev-p-mb-d.baboon-city.ts.net.evil.com`),
 * never a name a server answered with. The shell spelling of the same list is `ops_tailnet_names` in
 * `scripts/stable-ops-lib.sh`; `tailnet-identities.guard.test.ts` keeps the two equal.
 *
 * Platform-agnostic: the probe is injected, so the CLI (node fetch), the web (browser fetch) and a
 * test all use this one engine.
 */

/** This Mac's tailnet names, in the order tried when nothing is known: the first is the original. */
export const TAILNET_IDENTITY_HOSTS: readonly string[] = [
  'ev-p-mb-d.baboon-city.ts.net',
  'ev-p-mb-d-evp.baboon-city.ts.net',
];

/** How long one probe may take. A switched-off name times out, so this is what a switch costs once. */
export const TAILNET_PROBE_TIMEOUT_MS = 2500;

/** How long a good answer is believed before the name is asked again. */
export const TAILNET_GOOD_NAME_TTL_MS = 5 * 60 * 1000;

function parse(url: string): URL | null {
  try {
    const parsed = new URL(url.trim());
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
    if (parsed.username !== '' || parsed.password !== '') return null;
    return parsed;
  } catch {
    return null;
  }
}

/** The listed name a URL points at (exact, case-insensitive), or null for any other host. */
export function tailnetIdentityOf(url: string): string | null {
  const parsed = parse(url);
  if (!parsed) return null;
  const host = parsed.hostname.toLowerCase();
  return TAILNET_IDENTITY_HOSTS.includes(host) ? host : null;
}

/** Whether a host name is exactly one of this Mac's names. */
export function isTailnetIdentityHost(host: string): boolean {
  return TAILNET_IDENTITY_HOSTS.includes(host.trim().toLowerCase());
}

/** The same URL on another listed name. Scheme, port, path and query stay; the name is the only change. */
export function withTailnetIdentity(url: string, host: string): string | null {
  const parsed = parse(url);
  if (!parsed || !isTailnetIdentityHost(host) || tailnetIdentityOf(url) === null) return null;
  parsed.hostname = host.trim().toLowerCase();
  return parsed.toString().replace(/\/$/, url.endsWith('/') ? '/' : '');
}

/**
 * Every listed form of a URL, the one asked for or remembered first. A URL on a host that is not
 * listed has one form: itself. Nothing outside the list is ever produced.
 */
export function tailnetCandidates(url: string, lastGood?: string | null): string[] {
  const own = tailnetIdentityOf(url);
  if (own === null) return [url];
  const preferred =
    lastGood && isTailnetIdentityHost(lastGood) ? lastGood.trim().toLowerCase() : own;
  const hosts = [preferred, ...TAILNET_IDENTITY_HOSTS.filter((host) => host !== preferred)];
  const out: string[] = [];
  for (const host of hosts) {
    const candidate = withTailnetIdentity(url, host);
    if (candidate !== null) out.push(candidate);
  }
  return out;
}

/** `<origin>/api/health` for any URL on the deployment: the one address every probe asks. */
export function tailnetHealthUrl(url: string): string | null {
  const parsed = parse(url);
  return parsed ? `${parsed.origin}/api/health` : null;
}

export interface ITailnetResolverOptions {
  /** True when the address answered `/api/health` with success within the bound. Never throws. */
  probe: (healthUrl: string) => Promise<boolean>;
  now?: () => number;
  ttlMs?: number;
  /** A last good host remembered from an earlier run; used first, and only if it is listed. */
  initialGoodHost?: string | null;
  /** The initial host is known to answer right now (a page served from it), so it is not probed first. */
  initialGoodIsFresh?: boolean;
  /** Told when the answering host changes, so a caller can persist it. */
  onGoodHost?: (host: string) => void;
}

export interface ITailnetResolver {
  /**
   * The URL to use now: the remembered name while its answer is fresh; otherwise the first listed
   * form that answers. When none answers, the URL as given, so the caller's own error says what failed.
   */
  resolve(url: string): Promise<string>;
  /**
   * The URL on the remembered name, synchronously, when its answer is fresh; otherwise null. For a
   * caller that must not wait (a request that has to leave in the call that makes it).
   */
  fresh(url: string): string | null;
  /** Forget the remembered answer: a request on it failed, so the next call asks again. */
  forget(): void;
  goodHost(): string | null;
}

export function createTailnetResolver(options: ITailnetResolverOptions): ITailnetResolver {
  const now = options.now ?? (() => Date.now());
  const ttlMs = options.ttlMs ?? TAILNET_GOOD_NAME_TTL_MS;
  let good: string | null =
    options.initialGoodHost && isTailnetIdentityHost(options.initialGoodHost)
      ? options.initialGoodHost.toLowerCase()
      : null;
  let goodAt = good !== null && options.initialGoodIsFresh ? now() : Number.NEGATIVE_INFINITY;
  const inflight = new Map<string, Promise<string>>();

  const ask = async (url: string): Promise<string> => {
    for (const candidate of tailnetCandidates(url, good)) {
      const health = tailnetHealthUrl(candidate);
      if (health === null) continue;
      let answered = false;
      try {
        answered = await options.probe(health);
      } catch {
        answered = false;
      }
      if (answered) {
        const host = tailnetIdentityOf(candidate);
        if (host !== null && host !== good) options.onGoodHost?.(host);
        good = host;
        goodAt = now();
        return candidate;
      }
    }
    return url;
  };

  const freshUrl = (url: string): string | null =>
    good !== null && now() - goodAt < ttlMs ? withTailnetIdentity(url, good) : null;

  return {
    fresh(url) {
      return tailnetIdentityOf(url) === null ? url : freshUrl(url);
    },
    async resolve(url) {
      if (tailnetIdentityOf(url) === null) return url;
      const known = freshUrl(url);
      if (known !== null) return known;
      const key = new URL(url).origin;
      let pending = inflight.get(key);
      if (!pending) {
        pending = ask(url).finally(() => inflight.delete(key));
        inflight.set(key, pending);
      }
      const answer = await pending;
      // One in-flight probe serves every caller of that origin, but each gets its own path.
      const host = tailnetIdentityOf(answer);
      return host === null ? url : (withTailnetIdentity(url, host) ?? url);
    },
    forget() {
      goodAt = Number.NEGATIVE_INFINITY;
    },
    goodHost() {
      return good;
    },
  };
}
